import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_PREFIX, WS_CLOSE_SESSION_ENDED, WsEvent, type Device } from '@findmy/shared';
import { endSession, keys, upsertCommand } from '../api/queries';
import { applyEvent } from './applyEvent';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting';

const MAX_BACKOFF_MS = 30_000;

/**
 * Keeps the device list current over the server's WebSocket feed, reconnecting with
 * backoff. On every (re)connect the list is refetched, so nothing missed while
 * disconnected is lost.
 */
export function useLiveFeed(enabled: boolean): LiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');

  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;

    const connect = () => {
      const url = new URL(`${API_PREFIX}/ws`, window.location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(url);

      socket.onopen = () => {
        attempt = 0;
        setStatus('live');
        void queryClient.invalidateQueries({ queryKey: keys.devices });
      };

      socket.onmessage = (message) => {
        const parsed = WsEvent.safeParse(JSON.parse(String(message.data)));
        if (!parsed.success) return;
        if (parsed.data.type === 'command.updated') {
          upsertCommand(queryClient, parsed.data.command);
          return;
        }
        if (parsed.data.type === 'device.updated') {
          // A new fix: any history on screen that reaches up to now gains a point.
          const { device } = parsed.data;
          const before = queryClient.getQueryData<Device[]>(keys.devices)?.find((d) => d.id === device.id);
          if (before?.lastLocation?.id !== device.lastLocation?.id) {
            void queryClient.invalidateQueries({ queryKey: keys.history(device.id) });
          }
        }
        queryClient.setQueryData<Device[]>(keys.devices, (devices) => applyEvent(devices, parsed.data));
      };

      socket.onclose = (event) => {
        if (stopped) return;
        if (event.code === WS_CLOSE_SESSION_ENDED) {
          endSession(queryClient);
          return;
        }
        setStatus('reconnecting');
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt) * (0.75 + Math.random() / 2);
        attempt++;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      socket?.close();
    };
  }, [enabled, queryClient]);

  return status;
}
